import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { ToolbarModule } from 'primeng/toolbar';
import { InputNumberModule } from 'primeng/inputnumber';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ClaimContractService } from '../claim-contract.service';
import { HttpParams } from '@angular/common/http';
import { forkJoin, Observable, Subscription } from 'rxjs';

import { Business } from '../../master-data/business/business.model';
import { BusinessService } from '../../master-data/business/business.service';

import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { IftaLabelModule } from 'primeng/iftalabel';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { ClaimContract } from '../claim-contract.model';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AppUser } from '../../users/user.model';
import { AuthenticationService } from '../../services/authentication.service';
import { DatePicker } from 'primeng/datepicker';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SuppliersService } from '../../master-data/suppliers/suppliers.service';
import { DiscountType } from '../duscount-types';
import { ActivityTypeService } from '../../master-data/activity-type/activity-type.service';
import { ActivityType } from '../../master-data/activity-type/activity-type.model';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-claim-contract-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    CalendarModule,
    TagModule,
    ToastModule,
    ToolbarModule,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    ChipModule,
    DropdownModule,
    IftaLabelModule,
    ConfirmDialog,
    DatePicker,
    ProgressSpinner,
    AppDatePipe,
  ],

  providers: [MessageService, ConfirmationService],
  templateUrl: './claim-contract-list.component.html',
  styleUrls: ['./claim-contract-list.component.css'],
})
export class ClaimContractListComponent implements OnInit {
  private readonly maxExportRecords = 2000;
  private readonly claimContractExportHeaders = [
    'Business Code',
    'Business',
    'Supplier Number',
    'Supplier',
    'Activity Type',
    'Discount Type',
    'Claim Percent',
    'Start Date',
    'End Date',
    'AP GL Segment 1',
    'AP GL Segment 2',
    'AP GL Segment 3',
    'AP GL Segment 4',
    'AP GL Segment 5',
    'AP GL Segment 6',
    'AP GL Segment 7',
    'AP GL Segment 8',
    'AP GL Segment 9',
    'AP GL Segment 10',
    'Claim Number',
    'Created By',
  ];

  @ViewChild('dt') dt!: Table;
  claimContracts: ClaimContract[] = [];
  totalRecords = 0;
  isLoading: boolean = false;
  page: number = 0;
  rows: number = 25;
  params: HttpParams = new HttpParams();

  filterForm!: FormGroup;
  private filterSub!: Subscription;
  isSubmitted: boolean = false;
  user: AppUser | null = null;
  user$: Observable<AppUser | null>;

  // Options for filter dropdowns
  activityTypes: ActivityType[] = [];
  filteredActivityTypes: ActivityType[] = [];

  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];

  suppliers: Supplier[] = [];
  filteredSuppliers: Supplier[] = [];

  activityTypefilterValue: string = '';
  BusinessfilterValue: string = '';
  SupplierfilterValue: string = '';

  discountTypes: { label: string; value: string }[] = Object.values(
    DiscountType
  ).map((t) => ({ label: t, value: t }));

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private claimContractService: ClaimContractService,
    private messageService: MessageService,
    private businessService: BusinessService,
    private activityTypeService: ActivityTypeService,
    private supplierService: SuppliersService,
    private fb: FormBuilder
  ) {
    this.user$ = this.authService.getAuthState();
  }

  ngOnInit() {
    this.user$.subscribe((user) => {
      if (user) {
        this.user = user;
      } else {
        this.user = null;
      }
    });

    this.buildFilterForm();
    this.loadDropdownData();
  }

  buildFilterForm() {
    this.filterForm = this.fb.group({
      claimContractNo: [''],
      startDate: [null],
      endDate: [null],
      activityType: [null],
      business: [null],
      supplier: [null],
      discountType: [null],
    });
  }

  loadClaimContracts(event: TableLazyLoadEvent = {}) {
    this.isLoading = true;

    this.params = this.params.set(
      'page',
      event.first
        ? (event.first / (event.rows || this.rows)).toString()
        : this.page.toString()
    );
    this.params = this.params.set(
      'size',
      event.rows ? event.rows.toString() : this.rows.toString()
    );

    if (event.sortField) {
      this.params = this.params.set(
        'sort',
        `${event.sortField},${event.sortOrder === 1 ? 'asc' : 'desc'}`
      );
    } else {
      this.params = this.params.set('sort', 'id,desc');
    }

    const filters = this.filterForm.value;

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== null && value !== '' && value !== undefined) {
        this.params = this.params.set(key, value);
      }
    });

    this.claimContractService.searchClaimContracts(this.params).subscribe({
      next: (page) => {
        this.claimContracts = page.content;
        this.totalRecords = page.totalElements;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load claims',
        });
      },
    });
  }

  onSearch() {
    this.dt.reset();
  }

  onReset() {
    this.filterForm.reset();
    this.dt.reset();
  }

  submitForm() {
    this.isSubmitted = true;
    if (this.filterForm.valid) {  
      this.isLoading = true;

      const filters: any = {
        claimContractNo: this.filterForm.value.claimContractNo,
        startDate: this.filterForm.value.startDate
          ? this.formatDateToLocal(this.filterForm.value.startDate)
          : null,
        endDate: this.filterForm.value.endDate
          ? this.formatDateToLocal(this.filterForm.value.endDate)
          : null,
        activityType: this.filterForm.value.activityType?.id,
        business: this.filterForm.value.business?.id,
        supplier: this.filterForm.value.supplier?.id,
        discountType: this.filterForm.value.discountType,
      };

      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value !== null && value !== '' && value !== undefined) {
          this.params = this.params.set(key, value.toString());
        }
      });

      this.claimContractService.searchClaimContracts(this.params).subscribe({
        next: (data) => {
          this.isLoading = false;
          this.claimContracts = data.content;   
          this.totalRecords = data.totalElements;
        },
        error: (err) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load contracts.',
          });
        },
      });
    }
  }

  formatDateToLocal(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  loadDropdownData(): void {
    forkJoin({
      businesses: this.businessService.getAllBusinesss({
        page: 0,
        size: 10000,
      }),
      suppliers: this.supplierService.getAllSuppliers({ page: 0, size: 10000 }),
      activityTypes: this.activityTypeService.getAllActivityTypes({
        page: 0,
        size: 1000,
      }),
    }).subscribe({
      next: (data) => {
        this.activityTypes = data.activityTypes.content;
        this.filteredActivityTypes = data.activityTypes.content;
        if (this.user?.roles[0]?.roleName === 'Admin') {
          this.businesses = data.businesses.content;
          this.filteredBusinesses = data.businesses.content;
          this.suppliers = data.suppliers.content;
          this.filteredSuppliers = data.suppliers.content;
        } else {
          this.businesses =
            this.user?.businessIds && this.user.businessIds.length > 0
              ? this.businesses.filter((b) =>
                  this.user?.businessIds?.includes(b.id)
                )
              : [];
          this.filteredBusinesses =
            this.user?.businessIds && this.user.businessIds.length > 0
              ? this.businesses.filter((b) =>
                  this.user?.businessIds?.includes(b.id)
                )
              : [];

          this.suppliers =
            this.user?.supplierIds && this.user.supplierIds.length > 0
              ? this.suppliers.filter((s) =>
                  this.user?.supplierIds?.includes(s.id)
                )
              : [];
          this.filteredSuppliers =
            this.user?.supplierIds && this.user.supplierIds.length > 0
              ? this.suppliers.filter((s) =>
                  this.user?.supplierIds?.includes(s.id)
                )
              : [];
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load filter options.',
        });
        console.error('Error isLoading dropdown data:', err);
      },
    });
  }

  filterActivityTypes(event: any) {
    this.activityTypefilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredActivityTypes = this.activityTypes;
    } else {
      this.filteredActivityTypes = this.activityTypes.filter((activityType) =>
        activityType.name
          .toLowerCase()
          .includes(event.target.value.toLowerCase())
      );
    }
  }

  filterBusinesses(event: any) {
    this.BusinessfilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredBusinesses = this.businesses;
    } else {
      this.filteredBusinesses = this.businesses.filter(
        (business) =>
          business.businessName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (business.businessCode &&
            business.businessCode
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  filterSuppliers(event: any) {
    this.SupplierfilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredSuppliers = this.suppliers;
    } else {
      this.filteredSuppliers = this.suppliers.filter(
        (supplier) =>
          supplier.supplierName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (supplier.supplierNumber
            ? supplier.supplierNumber
                .toLowerCase()
                .includes(event.target.value.toLowerCase())
            : false)
      );
    }
  }

  onLazyLoad(event: any) {
    this.loadClaimContracts(event);
  }

  NewClaimContract(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['claim-contract', 'add'] } },
    ]);
  }

  ViewClaimContract(claim: ClaimContract): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['claim-contract', 'view', claim.id] } },
    ]);
  }

  EditClaimContract(claim: ClaimContract): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['claim-contract', 'edit', claim.id] } },
    ]);
  }

  clear(table: Table): void {
    table.clear();
  }

  exportExcel(): void {
    this.isLoading = true;

    const exportParams = this.buildExportParams();
    this.claimContractService.searchClaimContracts(exportParams).subscribe({
      next: (page) => {
        this.isLoading = false;

        const contracts = (page.content ?? []).slice(0, this.maxExportRecords);
        if (!contracts.length) {
          this.messageService.add({
            severity: 'info',
            summary: 'Nothing to export',
            detail: 'No claim contracts match the current filters.',
          });
          return;
        }

        this.downloadClaimContractExport(contracts);

        if (page.totalElements > this.maxExportRecords) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Export limited',
            detail: `Only the first ${this.maxExportRecords.toLocaleString()} of ${page.totalElements.toLocaleString()} matching records were exported.`,
            life: 10000,
          });
        }
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Export failed',
          detail: 'Failed to load claim contracts for export.',
        });
      },
    });
  }

  private buildExportParams(): HttpParams {
    const filters = this.filterForm?.value ?? {};
    const filterParams: Record<string, string | null | undefined> = {
      claimContractNo: filters.claimContractNo,
      startDate: filters.startDate
        ? this.formatDateToLocal(filters.startDate)
        : null,
      endDate: filters.endDate
        ? this.formatDateToLocal(filters.endDate)
        : null,
      activityType: filters.activityType?.id,
      business: filters.business?.id,
      supplier: filters.supplier?.id,
      discountType: filters.discountType,
    };

    let params = new HttpParams()
      .set('page', '0')
      .set('size', this.maxExportRecords.toString())
      .set('sort', 'id,desc');

    Object.entries(filterParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return params;
  }

  private downloadClaimContractExport(contracts: ClaimContract[]): void {
    const rows = contracts.map((claimContract) =>
      this.mapClaimContractToExportRow(claimContract)
    );
    const worksheet = XLSX.utils.aoa_to_sheet([
      this.claimContractExportHeaders,
      ...rows,
    ]);

    worksheet['!cols'] = [
      { wch: 12.75 },
      { wch: 20.33 },
      { wch: 14.33 },
      { wch: 43.08 },
      { wch: 17.5 },
      { wch: 14.5 },
      { wch: 12.08 },
      { wch: 11.08 },
      { wch: 8.25 },
      ...Array.from({ length: 9 }, () => ({ wch: 14.75 })),
      { wch: 15.75 },
      { wch: 18 },
      { wch: 24 },
    ];
    worksheet['!autofilter'] = {
      ref: `A1:U${rows.length + 1}`,
    };

    for (let rowNumber = 2; rowNumber <= rows.length + 1; rowNumber += 1) {
      for (const column of ['H', 'I']) {
        const cell = worksheet[`${column}${rowNumber}`];
        if (cell) {
          cell.z = 'd-mmm-yy';
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Claim Contract Template');
    XLSX.writeFile(workbook, 'claim_contract_export.xlsx', { cellDates: true });
  }

  private mapClaimContractToExportRow(claimContract: ClaimContract): unknown[] {
    return [
      claimContract.business?.businessCode ??
        claimContract.businessNumber ??
        claimContract.businessCode ??
        '',
      claimContract.business?.businessName ?? '',
      claimContract.supplier?.supplierNumber ?? claimContract.supplierNumber ?? '',
      claimContract.supplier?.supplierName ?? '',
      claimContract.activityType?.name ?? '',
      claimContract.discountType ?? '',
      claimContract.claimPercent ?? '',
      this.toExcelDate(claimContract.startDate),
      this.toExcelDate(claimContract.endDate),
      this.toExportText(claimContract.apGlSegment1),
      this.toExportText(claimContract.apGlSegment2),
      this.toExportText(claimContract.apGlSegment3),
      this.toExportText(claimContract.apGlSegment4),
      this.toExportText(claimContract.apGlSegment5),
      this.toExportText(claimContract.apGlSegment6),
      this.toExportText(claimContract.apGlSegment7),
      this.toExportText(claimContract.apGlSegment8),
      this.toExportText(claimContract.apGlSegment9),
      this.toExportText(claimContract.apGlSegment10),
      claimContract.claimContractNo ?? '',
      claimContract.createdBy?.fullName ?? '',
    ];
  }

  private toExportText(value: string | number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value);
  }

  private toExcelDate(value: Date | string | null | undefined): Date | '' {
    if (!value) {
      return '';
    }

    if (value instanceof Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) {
      return '';
    }

    return new Date(year, month - 1, day);
  }
}
